import React, { useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ENDPOINTS } from '../../helpers/constants';
import { postToday } from './helpers';

const nextActions = ['Call customer', 'Text customer', 'Schedule appointment', 'Send application'];
const moveTimelines = ['0-90 days', '3-6 months', '6+ months', 'Not sure'];

function ChoiceButtons({ options, value, onChange }) {
  return (
    <div className="d-flex">
      {options.map(option => (
        <Button
          key={option}
          type="button"
          variant={value === option ? 'primary' : 'outline-secondary'}
          className="flex-fill mr-2"
          onClick={() => onChange(value === option ? '' : option)}
        >
          {option}
        </Button>
      ))}
    </div>
  );
}

function QuickAddModal({ leadSources, statuses, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [buyer, setBuyer] = useState('');
  const [cellPhone, setCellPhone] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [leadSource, setLeadSource] = useState(
    leadSources.find(source => /lead/i.test(source)) || leadSources[0] || ''
  );
  const [status, setStatus] = useState(statuses.includes('New Leads') ? 'New Leads' : statuses[0] || 'New Leads');
  const [land, setLand] = useState('');
  const [homeType, setHomeType] = useState('');
  const [targetPayment, setTargetPayment] = useState('');
  const [timeline, setTimeline] = useState('0-90 days');
  const [note, setNote] = useState('');
  const [nextAction, setNextAction] = useState(nextActions[0]);
  const [followUpDate, setFollowUpDate] = useState('');
  const [dueTime, setDueTime] = useState('09:30');
  const [saving, setSaving] = useState(false);
  const [matches, setMatches] = useState([]);
  const [phoneError, setPhoneError] = useState('');
  const history = useHistory();

  const phoneOk = cellPhone.replace(/\D/g, '').length >= 7;
  const canContinue = buyer.trim().length > 1 && leadSource;

  const continueStep = event => {
    event.preventDefault();
    if (!phoneOk) {
      setPhoneError('Enter a cell phone with at least 7 digits.');
      return;
    }
    setPhoneError('');
    if (canContinue) setStep(2);
  };

  const save = async event => {
    event.preventDefault();
    if (!nextAction) return;
    const details = [
      note.trim(),
      land && `Land: ${land}`,
      homeType && `Looking for: ${homeType}`,
      targetPayment.trim() && `Target payment: ${targetPayment.trim()}`,
      timeline && `Move timeline: ${timeline}`
    ].filter(Boolean);
    setSaving(true);
    const response = await postToday(ENDPOINTS.CONTACTS_TODAY_ADD, {
      buyer: buyer.trim(),
      cellPhone: cellPhone.trim(),
      buyerEmail: buyerEmail.trim(),
      leadSource,
      status,
      personalNotes: details.join('\n'),
      nextAction,
      followUpDate,
      dueTime
    });
    setSaving(false);
    if (!response) return;
    if (!response.created) {
      setMatches(response.matches || []);
      setStep(1);
      return;
    }
    toast.success(`${buyer.trim()} added`);
    onCreated();
    onClose();
  };

  return (
    <Modal show onHide={onClose} centered>
      <Form onSubmit={step === 1 ? continueStep : save}>
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="text-muted small font-weight-normal">Step {step} of 2</div>
            {step === 1 ? 'Add a new customer' : 'What do we know?'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {step === 1 ? (
            <>
              <p className="bg-light border rounded p-2 small">
                Keep this fast. Capture enough to start the conversation. The full record can be completed later.
              </p>
              {matches.length > 0 && (
                <div className="alert alert-warning">
                  <div className="font-weight-bold mb-2">This phone or email already exists.</div>
                  {matches.map(match => (
                    <div key={match._id} className="mb-2">
                      <div>{match.buyer} · {match.status}</div>
                      <div className="small text-muted">{match.cellPhone} {match.buyerEmail}</div>
                      {match.canOpen && (
                        <Button variant="link" className="p-0" onClick={() => history.push(`/crm/${match._id}`)}>
                          Open record
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <Form.Group>
                <Form.Label>Full buyer name</Form.Label>
                <Form.Control value={buyer} onChange={event => setBuyer(event.target.value)} required autoFocus />
              </Form.Group>
              <Form.Group>
                <Form.Label>Cell phone</Form.Label>
                <Form.Control
                  type="tel"
                  value={cellPhone}
                  isInvalid={!!phoneError}
                  onChange={event => {
                    setCellPhone(event.target.value);
                    setPhoneError('');
                  }}
                />
                <Form.Control.Feedback type="invalid">{phoneError}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group>
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" value={buyerEmail} onChange={event => setBuyerEmail(event.target.value)} placeholder="Optional" />
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label>Lead source</Form.Label>
                <Form.Control as="select" value={leadSource} onChange={event => setLeadSource(event.target.value)} required>
                  {leadSources.map(source => <option key={source}>{source}</option>)}
                </Form.Control>
              </Form.Group>
            </>
          ) : (
            <>
              <p className="bg-light border rounded p-2 small mb-3">
                <strong>{buyer}</strong><br />
                {cellPhone} · {leadSource}
              </p>
              <Form.Group>
                <Form.Label>Customer stage</Form.Label>
                <Form.Control as="select" value={status} onChange={event => setStatus(event.target.value)}>
                  {(statuses.length ? statuses : ['New Leads']).map(item => <option key={item}>{item}</option>)}
                </Form.Control>
              </Form.Group>
              <Form.Group>
                <Form.Label>Land situation</Form.Label>
                <ChoiceButtons options={['Owns land', 'Needs land']} value={land} onChange={setLand} />
              </Form.Group>
              <Form.Group>
                <Form.Label>What are they looking for?</Form.Label>
                <ChoiceButtons options={['Singlewide', 'Doublewide']} value={homeType} onChange={setHomeType} />
              </Form.Group>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Target payment</Form.Label>
                    <Form.Control value={targetPayment} onChange={event => setTargetPayment(event.target.value)} placeholder="$2,200" />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Move timeline</Form.Label>
                    <Form.Control as="select" value={timeline} onChange={event => setTimeline(event.target.value)}>
                      {moveTimelines.map(item => <option key={item}>{item}</option>)}
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group>
                <Form.Label>Conversation note</Form.Label>
                <Form.Control as="textarea" rows={3} value={note} onChange={event => setNote(event.target.value)} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Next action</Form.Label>
                <Form.Control as="select" value={nextAction} onChange={event => setNextAction(event.target.value)} required>
                  {nextActions.map(item => <option key={item}>{item}</option>)}
                </Form.Control>
              </Form.Group>
              <Row>
                <Col>
                  <Form.Group className="mb-0">
                    <Form.Label>Date</Form.Label>
                    <Form.Control type="date" value={followUpDate} onChange={event => setFollowUpDate(event.target.value)} />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-0">
                    <Form.Label>Time</Form.Label>
                    <Form.Control type="time" value={dueTime} onChange={event => setDueTime(event.target.value)} />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {step === 1 ? (
            <Button variant="outline-dark" onClick={onClose}>Cancel</Button>
          ) : (
            <Button variant="outline-dark" onClick={() => setStep(1)}>Back</Button>
          )}
          <Button variant="success" type="submit" disabled={step === 1 ? !canContinue : saving || !nextAction}>
            {step === 1 ? 'Continue' : saving ? 'Saving…' : 'Save customer'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default QuickAddModal;
